import { isEqual } from "lodash-es";
import { toRaw } from "vue";
import { type NavigationGuard, type RouteLocation, useRouter } from "vue-router";
import yn from "yn";

import {
  DictionariesHelper,
  DictionaryMethodHelper,
  PersistentSearchFilterHelper,
  SearchFilterHelper,
  SearchHelper,
  SearchRouteHelper
} from "@/helpers";
import {
  useDictionariesStore,
  useDictsysInflectionSchemeStore,
  useSearchFilterStore,
  useSearchStore,
  useSuggestionsStore,
  useUserStore
} from "@/stores";
import type { DeepPartial, SearchFilter } from "@/types";

export function updateSearchStoreGuard(): NavigationGuard {
  return (to, from, next) => {
    updateSearchFilterStore(to);

    if (to.meta.searchViewType === "classic") {
      const { query, dictionaryId, allowSwitchDictionary, section } = to.query as Record<string, string | undefined>;

      if (query === undefined) {
        return next();
      }

      const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);

      updateSearchFilterStore(to);

      const searchStore = useSearchStore();
      const searchFilterStore = useSearchFilterStore();

      const currentSearch = {
        query: searchStore.query,
        filter: searchStore.filter,
        dictionaryId: searchStore.selectedDictionaryId,
        staticMetadata: toRaw(searchStore.staticMetadata),
        temporaryMetadata: toRaw(searchStore.temporaryMetadata),
        section: searchStore.section
      };

      const staticMetadata = searchRouteHelper.getStaticMetadata(to.query);
      const temporaryMetadata = searchRouteHelper.getTemporaryMetadata(to.query);

      const nextSearch = {
        query,
        filter: searchFilterStore.filter,
        dictionaryId,
        staticMetadata,
        temporaryMetadata,
        section
      };

      if (SearchHelper.hasSearchChanged(currentSearch, nextSearch) || from.meta.searchViewType === "compact") {
        resetStores();

        searchStore.classicSearch(
          query,
          searchFilterStore.filter,
          dictionaryId,
          staticMetadata,
          temporaryMetadata,
          section,
          yn(allowSwitchDictionary, { default: true })
        );
      }
    }

    if (to.meta.searchViewType === "compact") {
      const { query, dictionaryId } = to.query as Record<string, string | undefined>;

      if (query === undefined) {
        return next();
      }

      const searchStore = useSearchStore();
      const searchFilterStore = useSearchFilterStore();
      const dictionariesStore = useDictionariesStore();

      const currentSearch = {
        query: searchStore.query,
        dictionaryId: searchStore.selectedDictionaryId,
        filter: searchStore.filter
      };

      const nextSearch = {
        query,
        dictionaryId,
        filter: searchFilterStore.filter
      };

      const isStayingOnCompact = from.meta.searchViewType === "compact" && to.meta.searchViewType === "compact";
      const isSameQuery = currentSearch.query === nextSearch.query;
      const isSameFilter = !SearchHelper.hasFilterChanged(currentSearch.filter, nextSearch.filter);
      const isDeselectingDictionary = currentSearch.dictionaryId !== undefined && nextSearch.dictionaryId === undefined;
      const isExactResults = searchStore.results?.type === "exact";
      const currentSearchDictionary = currentSearch.dictionaryId
        ? dictionariesStore.getDictionaryById(currentSearch.dictionaryId)
        : undefined;

      if (
        isStayingOnCompact &&
        isSameQuery &&
        isSameFilter &&
        isDeselectingDictionary &&
        isExactResults &&
        currentSearchDictionary
      ) {
        const methodIds = DictionariesHelper.getMethods([currentSearchDictionary]).map((method) => method.id);

        const articlesInOtherDictionaries = [
          ...searchStore.articles.filter((article) => !methodIds.includes(article.methodId)),
          ...searchStore.upsellArticles.filter((article) => !methodIds.includes(article.methodId))
        ];

        // If there is any other dictionary with articles, we assume that a full search has already been made
        if (articlesInOtherDictionaries.length) {
          searchStore.selectedDictionaryId = undefined;
          return next();
        }
      }

      if (SearchHelper.hasSearchChanged(currentSearch, nextSearch, false) || from.meta.searchViewType === "classic") {
        resetStores();

        searchStore.compactSearch(query, searchFilterStore.filter, dictionaryId);
      }
    }

    next();
  };
}

function updateSearchFilterStore(route: RouteLocation): void {
  const dictionariesStore = useDictionariesStore();
  const searchFilterStore = useSearchFilterStore();

  let filter: DeepPartial<SearchFilter> | undefined;

  // On search routes, if there is no filter in the URL, we need to clear the persisted
  // cookie filter, to avoid it being too sticky
  if (route.meta.isSearchRoute && !route.query.filter) {
    PersistentSearchFilterHelper.removeFilter();
  }

  if (route.query.filter) {
    filter = route.query.filter as DeepPartial<SearchFilter>;
  } else {
    filter = PersistentSearchFilterHelper.loadFilter();
  }

  if (filter) {
    PersistentSearchFilterHelper.saveFilter(filter);

    const restoredFilter = SearchFilterHelper.restore(
      filter,
      DictionaryMethodHelper.getLanguages(dictionariesStore.methods)
    );

    // Avoid setting the same filter, as it may trigger unnecessary reactivity
    if (!isEqual(restoredFilter, searchFilterStore.filter)) {
      searchFilterStore.filter = restoredFilter;
    }
  } else {
    searchFilterStore.filter.sourceLanguage = undefined;
    searchFilterStore.filter.targetLanguage = undefined;
    PersistentSearchFilterHelper.removeFilter();
  }
}

function resetStores(): void {
  // We don't want to keep the old suggestions.
  const suggestionsStore = useSuggestionsStore();
  suggestionsStore.clearSuggestions();

  // We don't want to keep old inflection scheme opened states.
  const dictsysInflectionScheme = useDictsysInflectionSchemeStore();
  dictsysInflectionScheme.clearSchemeOpened();
}
